import { Host, h } from "@stencil/core";
import { PhpDate } from "../../utils/dates/PhpDate";
import { DateFormatString } from "../../utils/dates/DateFormatString";
import { timezones } from "../../utils/timezones";
import { FallbackRenderInfo } from "../../utils/FallbackRenderInfo";
function renderInput(input, field) {
    if (field === 'timezone') {
        input = Object.assign({ additionalSettings: {} }, input);
        input.additionalSettings.options = timezones.map((t) => {
            return { name: t.timezone_id, value: t.timezone_id };
        });
        return input.renderInfo.renderSingleInput(['date-timezone', 'select'], input);
    }
    if (field === 'display') {
        return input.renderInfo.renderSingleInput(['date-display', 'text'], input);
    }
    return input.renderInfo.renderSingleInput(['date-' + field, 'integer', 'number', 'text'], input);
}
export class ApiePhpDateInput {
    constructor() {
        this.name = undefined;
        this.value = undefined;
        this.disabled = false;
        this.dateFormat = 'Y-m-d\\TH:i';
        this.showDatePicker = false;
        this.isTouched = false;
        this.internalDate = new PhpDate('', '', '', '', '', '', '', Intl.DateTimeFormat().resolvedOptions().timeZone);
        this.compiledDateformat = undefined;
        this.renderInputFn = renderInput;
        this.renderInfo = new FallbackRenderInfo();
    }
    toggleDatePicker() {
        this.showDatePicker = !this.showDatePicker;
    }
    componentWillLoad() {
        this.compiledDateformat = new DateFormatString(this.dateFormat);
        if (this.value) {
            this.internalDate = this.compiledDateformat.createFromString(this.value, this.internalDate);
        }
        this.checkValue();
    }
    updateInternalDate() {
        if (this.value) {
            this.internalDate = this.compiledDateformat.createFromString(this.value, this.internalDate);
        }
        this.checkValue();
    }
    updateDateFormat() {
        this.compiledDateformat = new DateFormatString(this.dateFormat);
        this.updateInternalDate();
    }
    checkValue() {
        const _value = this.compiledDateformat.isValid(this.internalDate)
            ? this.compiledDateformat.convertToString(this.internalDate)
            : null;
        if (_value !== this.value) {
            this.value = _value;
            this.change.emit(this.value);
        }
    }
    update(key, value) {
        this.internalDate[key] = value;
        this.internalDate = this.internalDate.clone();
        this.checkValue();
    }
    async updateToCurrentTime() {
        this.internalDate = PhpDate.createFromLocalDate(); // todo correct timezone?
        this.checkValue();
        await Promise.resolve();
    }
    get displayHourFields() {
        return this.compiledDateformat.displayHours
            || this.compiledDateformat.displayMinutes
            || this.compiledDateformat.displaySeconds
            || this.compiledDateformat.displayMilliseconds
            || this.compiledDateformat.displayMicroseconds;
    }
    get displayDateFields() {
        return this.compiledDateformat.displayDate
            || this.compiledDateformat.displayMonth
            || this.compiledDateformat.displayYear;
    }
    renderField(fieldName) {
        const input = {
            name: fieldName,
            value: this.internalDate[fieldName],
            disabled: this.disabled,
            valueChanged: (newValue) => this.update(fieldName, newValue),
            renderInfo: this.renderInfo,
            validationResult: {
                valid: this.compiledDateformat.isValid(this.internalDate),
                messages: []
            },
            serverValidationError: {},
            currentFieldWrapper: (content) => content,
            touched: this.isTouched,
            onTouched: () => { this.isTouched = true; this.touched.emit(); }
        };
        return this.renderInputFn(input, fieldName);
    }
    renderDateValue() {
        const input = {
            name: this.name,
            value: this.compiledDateformat.convertToString(this.internalDate),
            disabled: this.disabled,
            valueChanged: (_newValue) => { },
            renderInfo: this.renderInfo,
            validationResult: {
                valid: true,
                messages: []
            },
            serverValidationError: {},
            currentFieldWrapper: (content) => content,
            touched: this.isTouched,
            onTouched: () => { this.isTouched = true; this.touched.emit(); }
        };
        const res = this.renderInputFn(input, 'display');
        if (Array.isArray(res)) {
            res.forEach((r) => {
                if (r === null || r === void 0 ? void 0 : r.$attrs$) {
                    r.$attrs$.readonly = true;
                }
            });
        }
        else if (res === null || res === void 0 ? void 0 : res.$attrs$) {
            res.$attrs$.readonly = true;
        }
        return res;
    }
    renderTimeFields() {
        // Format date and time separately with Intl.DateTimeFormat
        const dateFormat = new Intl.DateTimeFormat(navigator.language, {
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
        }).formatToParts(new Date);
        // Extract order from date parts
        const dateParts = [];
        dateFormat.forEach(part => {
            if (part.type === 'hour' && this.compiledDateformat.displayHours) {
                dateParts.push(this.renderField('hours'));
            }
            if (part.type === 'minute' && this.compiledDateformat.displayMinutes) {
                dateParts.push(this.renderField('minutes'));
            }
            if (part.type === 'second') {
                if (this.compiledDateformat.displaySeconds) {
                    dateParts.push(this.renderField('seconds'));
                }
                if (this.compiledDateformat.displayMicroseconds) {
                    dateParts.push(this.renderField('microseconds'));
                }
                else if (this.compiledDateformat.displayMilliseconds) {
                    dateParts.push(this.renderField('milliseconds'));
                }
            }
        });
        return h("div", { style: { display: 'flex' } }, dateParts);
    }
    renderDateFields() {
        // Format date and time separately with Intl.DateTimeFormat
        const dateFormat = new Intl.DateTimeFormat(navigator.language, {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
        }).formatToParts(new Date);
        // Extract order from date parts
        const dateParts = [];
        dateFormat.forEach(part => {
            if (part.type === 'day' && this.compiledDateformat.displayDate) {
                dateParts.push(this.renderField('date'));
            }
            if (part.type === 'month' && this.compiledDateformat.displayMonth) {
                dateParts.push(this.renderField('month'));
            }
            if (part.type === 'year' && this.compiledDateformat.displayYear) {
                dateParts.push(this.renderField('year'));
            }
        });
        return h("div", { style: { display: 'flex' } }, dateParts);
    }
    renderNow() {
        return h("slot", { name: "now" }, h("button", { type: "button", onClick: () => this.updateToCurrentTime() }, "NOW"));
    }
    render() {
        const displayHours = this.displayHourFields;
        const displayDays = this.displayDateFields;
        const displayTimezone = this.compiledDateformat.displayTimezone;
        if (displayHours && !displayDays && !displayTimezone) {
            return h(Host, null, h("div", null, h("slot", { name: "hourfields" }, this.renderTimeFields())), this.renderNow());
        }
        if (displayDays && !displayHours && !displayTimezone) {
            return h(Host, null, h("div", null, h("slot", { name: "datefields" }, this.renderDateFields())), this.renderNow());
        }
        if (displayTimezone && !displayDays && !displayHours) {
            return h(Host, null, h("slot", { name: "timezone" }, h("div", { style: { display: 'flex' } }, this.renderField('timezone'))));
        }
        return h(Host, null, h("div", null, h("div", { onClick: () => !this.disabled && this.toggleDatePicker() }, h("slot", { name: "input" }, this.renderDateValue())), this.showDatePicker && h("div", { style: { display: 'flex', flexDirection: 'column' } }, displayHours && h("slot", { name: "hourfields" }, this.renderTimeFields()), displayDays && h("slot", { name: "datefields" }, this.renderDateFields()), displayTimezone && h("slot", { name: "timezone" }, h("div", { style: { display: 'flex' } }, this.renderField('timezone'))), this.renderNow())));
    }
    static get is() { return "apie-php-date-input"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["apie-php-date-input.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["apie-php-date-input.css"]
        };
    }
    static get properties() {
        return {
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "name",
                "reflect": false
            },
            "value": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "value",
                "reflect": true
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "dateFormat": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "date-format",
                "reflect": true,
                "defaultValue": "'Y-m-d\\\\TH:i'"
            },
            "internalDate": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "PhpDate",
                    "resolved": "PhpDate",
                    "references": {
                        "PhpDate": {
                            "location": "import",
                            "path": "../../utils/dates/PhpDate",
                            "id": "src/utils/dates/PhpDate.ts::PhpDate"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "defaultValue": "new PhpDate('', '', '', '', '', '', '', Intl.DateTimeFormat().resolvedOptions().timeZone)"
            },
            "compiledDateformat": {
                "type": "unknown",
                "mutable": true,
                "complexType": {
                    "original": "DateFormatString",
                    "resolved": "DateFormatString",
                    "references": {
                        "DateFormatString": {
                            "location": "import",
                            "path": "../../utils/dates/DateFormatString",
                            "id": "src/utils/dates/DateFormatString.ts::DateFormatString"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                }
            },
            "renderInputFn": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "RenderInputFn",
                    "resolved": "(input: InputState, field: \"display\" | TimeField) => VNode | VNode[]",
                    "references": {
                        "RenderInputFn": {
                            "location": "local",
                            "path": "/home/runner/work/apie-form-elements/apie-form-elements/src/components/apie-php-date-input/apie-php-date-input.tsx",
                            "id": "src/components/apie-php-date-input/apie-php-date-input.tsx::RenderInputFn"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "defaultValue": "renderInput"
            },
            "renderInfo": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "RenderInfo",
                    "resolved": "RenderInfo",
                    "references": {
                        "RenderInfo": {
                            "location": "import",
                            "path": "../../utils/RenderInfo",
                            "id": "src/utils/RenderInfo.tsx::RenderInfo"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "defaultValue": "new FallbackRenderInfo()"
            }
        };
    }
    static get states() {
        return {
            "showDatePicker": {},
            "isTouched": {}
        };
    }
    static get events() {
        return [{
                "method": "change",
                "name": "change",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }, {
                "method": "touched",
                "name": "touched",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "updateToCurrentTime": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get watchers() {
        return [{
                "propName": "value",
                "methodName": "updateInternalDate"
            }, {
                "propName": "compiledDateformat",
                "methodName": "updateInternalDate"
            }, {
                "propName": "dateFormat",
                "methodName": "updateDateFormat"
            }];
    }
}
//# sourceMappingURL=apie-php-date-input.js.map
